import React, { useState, useEffect } from "react";
import styles from "../GeneralInformation/GeneralInformation.module.scss";
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from "react-circular-progressbar";
import { useAppSelector } from "../../../redux/hooks";
import Icon from "../../common/Icon/Icon";
import { useTranslation } from "react-i18next";
import Editor from "../../common/Editor/Editor";
import {
  useCreateFoundationalInfoMutation,
  useGetFoundationalInfoDataQuery,
} from "../../../redux/services/LeaderFoundationalInfoAPI";
import { useProfileParams } from "../../../utils/common";
import ABOBackgroundLoader from "../../common/Loaders/ABOBackgroundLoader/ABOBackgroundLoader";

const FoundationalInfo = () => {
  const { t } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const { affiliateCode, aboNumber } = useProfileParams();
  const { data, isFetching, isLoading } = useGetFoundationalInfoDataQuery({
    affiliateCode,
    aboNumber,
  });

  const [createFoundationalInfo, { isLoading: isSaving }] =
    useCreateFoundationalInfoMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [foundationalInfoData, setFoundationalInfoData] = useState<any>({
    completionRate: 0,
    familyDetail: "",
    educationDetail: "",
    occupationDetail: "",
    interestAreaDetail: "",
    businessIssueDetail: "",
    founderFundamentalDetail: "",
    otherDetail: "",
  });
  useEffect(() => {
    if (data?.body) {
      const body = data.body;
      setFoundationalInfoData({
        completionRate: body.completionRate ?? 0,
        familyDetail: body.familyDetail ?? "",
        educationDetail: body.educationDetail ?? "",
        occupationDetail: body.occupationDetail ?? "",
        interestAreaDetail: body.hobbyDetail ?? "",
        businessIssueDetail: body.keyConcernDetail ?? "",
        founderFundamentalDetail: body.fundamentalDetail ?? "",
        otherDetail: body.otherDetail ?? "",
      });
    }
  }, [data]);

  const handleSave = async () => {
    try {
      await createFoundationalInfo({
        affiliateCode,
        aboNumber,
        payload: {
          familyDetail: foundationalInfoData.familyDetail,
          educationDetail: foundationalInfoData.educationDetail,
          occupationDetail: foundationalInfoData.occupationDetail,
          hobbyDetail: foundationalInfoData.interestAreaDetail,
          keyConcernDetail: foundationalInfoData.businessIssueDetail,
          fundamentalDetail: foundationalInfoData.founderFundamentalDetail,
          otherDetail: foundationalInfoData.otherDetail,
        },
      }).unwrap();
      setIsEditing(false);
    } catch (err) {
      console.error("Failed to save Foundational Info:", err);
    }
  };

  const handleCancel = () => {
    if (data?.body) {
      const body = data.body;
      setFoundationalInfoData({
        completionRate: body.completionRate ?? 0,
        familyDetail: body.familyDetail ?? "",
        educationDetail: body.educationDetail ?? "",
        occupationDetail: body.occupationDetail ?? "",
        interestAreaDetail: body.hobbyDetail ?? "",
        businessIssueDetail: body.keyConcernDetail ?? "",
        founderFundamentalDetail: body.fundamentalDetail ?? "",
        otherDetail: body.otherDetail ?? "",
      });
    }
    setIsEditing(false);
  };

  const handleChangeDesc = (keyName: string, value: string) => {
    setFoundationalInfoData((prev: any) => ({
      ...prev,
      [keyName]: value,
    }));
  };

  const fields = [
    { key: "familyDetail", label: t("family_info") },
    { key: "educationDetail", label: t("education_info") },
    { key: "occupationDetail", label: t("occupation") },
    { key: "interestAreaDetail", label: t("interest_area_info") },
    { key: "businessIssueDetail", label: t("business_issue_info") },
    { key: "founderFundamentalDetail", label: t("founder_fundamentals_info") },
    { key: "otherDetail", label: t("other_info") },
  ];
  if (isLoading || isSaving) {
    return (
      <ABOBackgroundLoader num={3} heading={t("foundational_information")} />
    );
  }
  return (
    <div className={styles.general_outer_wrapper}>
      <div className={styles.title_wrapper}>
        <div className={styles.title_inner}>
          <div className={styles.progress_bar}>
            <CircularProgressbarWithChildren
              value={foundationalInfoData.completionRate}
              strokeWidth={20}
              className={styles["circular_progress_gradient"]}
              styles={buildStyles({
                trailColor: currentTheme === "light" ? "#D9D9D9" : "#282828",
              })}
            />
          </div>
          <div className={styles.general_info_title}>
            {t("foundational_information")}
          </div>
        </div>
        <div className={styles.edit_icon_wrapper}>
          {isEditing ? (
            <>
              <span className={styles.cancel_text} onClick={handleCancel}>
                {t("btnCancel")}
              </span>
              <span
                className={styles.save_text}
                onClick={handleSave}
                style={{ opacity: isSaving ? 0.6 : 1 }}
              >
                {isSaving ? t("saving") : t("btnSave")}
              </span>
            </>
          ) : (
            <div
              className={styles.edit_wrapper}
              onClick={() => setIsEditing(true)}
            >
              <Icon
                name="edit_goal_icon"
                size={14}
                color={currentTheme === "light" ? "#2C2C2C" : "#D8D8D9"}
                className={styles.search_icon}
              />
              <span className={styles.edit_text}>{t("edit")}</span>
            </div>
          )}
        </div>
      </div>

      <div className={`${styles.general_inner_wrapper} ${styles.two_col}`}>
        <div className={styles.first_column_wrapper}>
          {fields.slice(0, 4).map(({ key, label }) => (
            <div className={styles.general_info} key={key}>
              <span className={styles.title}>{label}:</span>
              <Editor
                id={`editor_${key}`}
                testId={`editor_${key}_test`}
                isEditing={isEditing}
                value={foundationalInfoData[key] || ""}
                onChange={(value) => handleChangeDesc(key, value)}
                formatterBarPlaceholder={label}
                section={key}
                normalize={true}
                formatterBarWrapperClassName={styles.text_area_section}
                htmlToPdfContainerClassName={styles.description}
              />
            </div>
          ))}
        </div>

        <div className={styles.second_column_wrapper}>
          {fields.slice(4).map(({ key, label }) => (
            <div className={styles.general_info} key={key}>
              <span className={styles.title}>{label}:</span>
              <Editor
                id={`editor_${key}`}
                testId={`editor_${key}_test`}
                isEditing={isEditing}
                value={foundationalInfoData[key] || ""}
                onChange={(value) => handleChangeDesc(key, value)}
                formatterBarPlaceholder={label}
                section={key}
                normalize={true}
                formatterBarWrapperClassName={styles.text_area_section}
                htmlToPdfContainerClassName={styles.description}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FoundationalInfo;
